import I18n from 'I18n';
import { memo, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getIsUngatedForCampaignsSelectV2 } from '../utils/campaignGates';
import CampaignsPlatformSelectV2 from './CampaignsPlatformSelectV2/CampaignsPlatformSelectV2';
import CampaignSelectFormControl from 'campaigns-lib/components/campaignSelect/CampaignSelectFormControl';
import { hasCampaignsReadAccess, hasCampaignsWriteAccess } from './hooks/useFetchCampaignsAssociationSettings';
import { transformRpcResponseToFAS } from 'campaigns-utils-lib/campaignsAssociations';
import { useGetAllAssetConfigurationsQuery } from 'campaigns-lib/__generated__/chirp/com/hubspot/studio/backend/rpc/assetconfiguration/AssetConfigurationService.hooks';
import { GET_ASSOCIATED_CAMPAIGN_FIELD_NAME } from './hooks/useFetchAssociatedCampaign';
import { useHandleCampaignChange } from './hooks/useHandleCampaignChange';
import CampaignsPlatformSelectLabel from './CampaignsPlatformSelectLabel';
import { useCampaignsContext } from './CampaignsProvider';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { useFetchAssociationDefinition } from './hooks/useFetchAssociationDefinition';
import { useFetchAssociatedAssetObjectId } from './hooks/useFetchAssociatedAssetObjectId';
import { userInfoSync } from 'hub-http/userInfo';
import { Scopes } from 'campaigns-lib/constants/Permissions';
import { arrayContainsSameValues, flattenLoadedOptions, formatAssetLabel } from 'campaigns-lib/utils/campaignSelect';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import KnowledgeBaseButton from 'ui-addon-i18n/components/KnowledgeBaseButton';
import { useDataFetchingClient } from 'data-fetching-client';
import { CACHE_DEBOUNCE_DELAY } from '../constants/campaignSelect';
import { useDebounceCallback } from '../hooks/useDebounceCallback';
import { getIsAssetUngatedForOneToMany } from '../utils/campaignGates';
import { GET_ASSOCIATED_CAMPAIGNS_V2_FIELD_NAME, useFetchAssociatedCampaignsV2 } from './hooks/useFetchAssociatedCampaignsV2';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function CampaignsPlatformSelect({
  objectType,
  assetId,
  crmIdProperty = 'hs_object_id',
  values,
  onChange = () => {},
  save = true,
  editorSelect = true,
  FormLabel = undefined,
  withUIFloatingAlertList = true,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  disabled = false,
  disabledTooltip,
  formControlStyle,
  objectBuilderUiProps
}) {
  var _objectDefinition$nam;
  // To ensure the select is not removed when we invalidate the initial campaign cache on
  // change for the other campaign platform component to automatically update.
  const [hasEverLoaded, setHasEverLoaded] = useState(false);
  const {
    setIsOneToMany
  } = useCampaignsContext();
  const client = useDataFetchingClient();
  const loadedOptionsRef = useRef([]);
  const [selectedCampaignGuids, setSelectedCampaignGuids] = useState([]);
  const hasCampaignsToolAccess = useMemo(() => {
    try {
      return userInfoSync().user.scopes.includes(Scopes.CAMPAIGNS);
    } catch (error) {
      // Rely on BE to return the correct value
      return true;
    }
  }, []);
  const isAssetUngatedForOneToMany = useMemo(() => getIsAssetUngatedForOneToMany(objectType), [objectType]);
  const {
    data: assetConfigurationsData
  } = useGetAllAssetConfigurationsQuery();
  const associationSettings = useMemo(() => assetConfigurationsData ? transformRpcResponseToFAS(assetConfigurationsData) : undefined, [assetConfigurationsData]);
  const objectDefinition = useMemo(() => {
    var _assetConfigurationsD;
    return assetConfigurationsData === null || assetConfigurationsData === void 0 || (_assetConfigurationsD = assetConfigurationsData.objectPropertyDefinitions) === null || _assetConfigurationsD === void 0 ? void 0 : _assetConfigurationsD.find(def => def.name === objectType || def.objectTypeId === objectType);
  }, [assetConfigurationsData === null || assetConfigurationsData === void 0 ? void 0 : assetConfigurationsData.objectPropertyDefinitions, objectType]);
  const resolvedObjectType = (_objectDefinition$nam = objectDefinition === null || objectDefinition === void 0 ? void 0 : objectDefinition.name) !== null && _objectDefinition$nam !== void 0 ? _objectDefinition$nam : objectType;
  const {
    data: associationDefinition
  } = useFetchAssociationDefinition({
    associationSettings,
    objectType: resolvedObjectType
  });
  const isOneToMany = (associationDefinition === null || associationDefinition === void 0 ? void 0 : associationDefinition.cardinality) === 'ONE_TO_MANY' && isAssetUngatedForOneToMany;
  const {
    data: objectId
  } = useFetchAssociatedAssetObjectId({
    objectType,
    assetId,
    crmIdProperty,
    skip: !hasCampaignsToolAccess
  });
  const {
    data: {
      campaignGuids
    },
    loading
  } = useFetchAssociatedCampaignsV2({
    objectTypeId: resolvedObjectType,
    objectId,
    skip: !hasCampaignsToolAccess || !!values
  });
  useEffect(() => {
    if (campaignGuids && !values) {
      setSelectedCampaignGuids(prev => {
        //Don't change if local state and fetched values are the same
        const isSame = arrayContainsSameValues(prev, campaignGuids);
        if (isSame) return prev;

        //if they do differ, maintain the order of the elements currently in local state
        const existingInOrder = prev.filter(p => campaignGuids.includes(p));
        const newItems = campaignGuids.filter(f => !prev.includes(f));
        return [...existingInOrder, ...newItems];
      });
      setHasEverLoaded(true);
    } else if (values) {
      setSelectedCampaignGuids(values);
      setHasEverLoaded(true);
    }
  }, [campaignGuids, loading, values]);
  useEffect(() => {
    setIsOneToMany(isOneToMany);
  }, [isOneToMany, setIsOneToMany]);
  const debouncedCacheEvict = useDebounceCallback(() => {
    client.cache.evict({
      fieldName: GET_ASSOCIATED_CAMPAIGN_FIELD_NAME
    });
    client.cache.evict({
      fieldName: GET_ASSOCIATED_CAMPAIGNS_V2_FIELD_NAME
    });
  }, CACHE_DEBOUNCE_DELAY);
  const clearCache = useCallback(() => {
    debouncedCacheEvict();
  }, [debouncedCacheEvict]);
  const handleOptionsLoaded = useCallback(options => {
    loadedOptionsRef.current = flattenLoadedOptions(options);
  }, []);

  // Automatically save the selected campaign when it changes.
  const handleCampaignChange = useHandleCampaignChange({
    objectType,
    onChange,
    save,
    isOneToMany,
    selectedCampaignGuids,
    selectedAssetCrmId: objectId,
    setSelectedCampaignGuids,
    onSuccess: params => {
      var _params$updatedCampai, _params$selectedOptio, _ref, _loadedOptionsRef$cur, _params$selectedOptio2, _params$selectedOptio3;
      const asset = objectDefinition === null || objectDefinition === void 0 ? void 0 : objectDefinition.singularForm;
      const updatedGuids = (_params$updatedCampai = params === null || params === void 0 ? void 0 : params.updatedCampaignGuids) !== null && _params$updatedCampai !== void 0 ? _params$updatedCampai : [];
      const isRemoval = selectedCampaignGuids.length > updatedGuids.length || !(params !== null && params !== void 0 && (_params$selectedOptio = params.selectedOption) !== null && _params$selectedOptio !== void 0 && _params$selectedOptio.text);
      const removedGuid = selectedCampaignGuids.find(guid => !updatedGuids.includes(guid));
      const campaignName = isRemoval ? (_ref = (_loadedOptionsRef$cur = loadedOptionsRef.current.find(c => c.guid === removedGuid)) === null || _loadedOptionsRef$cur === void 0 ? void 0 : _loadedOptionsRef$cur.text) !== null && _ref !== void 0 ? _ref : '' : (_params$selectedOptio2 = params === null || params === void 0 || (_params$selectedOptio3 = params.selectedOption) === null || _params$selectedOptio3 === void 0 ? void 0 : _params$selectedOptio3.text) !== null && _params$selectedOptio2 !== void 0 ? _params$selectedOptio2 : '';
      const additionalCampaigns = Math.max(updatedGuids.length - 1, 0);
      const titleKey = isRemoval ? 'campaignSelect.success.platformRemoveTitle' : 'campaignSelect.success.platformAddTitle';
      const messageKey = isRemoval ? null : 'campaignSelect.success.platformAddMessage';
      FloatingAlertStore.addAlert({
        message: messageKey ? I18n.text(messageKey, {
          asset
        }) : undefined,
        titleText: I18n.text(titleKey, {
          asset,
          campaignName,
          additionalCampaigns
        }),
        type: 'info'
      });
    },
    onFailure: () => {
      FloatingAlertStore.addAlert({
        message: I18n.text('campaignSelect.failure.platformMessage', {
          asset: formatAssetLabel(objectType, objectDefinition === null || objectDefinition === void 0 ? void 0 : objectDefinition.singularForm)
        }),
        titleText: I18n.text('campaignSelect.failure.platformTitle'),
        type: 'danger'
      });
    }
  });
  if (!associationSettings || !associationSettings[resolvedObjectType] || !objectDefinition || !hasEverLoaded && assetId) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [withUIFloatingAlertList && /*#__PURE__*/_jsx(FloatingAlertList, {
      "data-test-id": "campaigns-lib-floating-alert-list"
    }), /*#__PURE__*/_jsx(CampaignSelectFormControl, {
      hasCampaignsReadAccess: hasCampaignsReadAccess(associationSettings, resolvedObjectType),
      hasCampaignsWriteAccess: hasCampaignsWriteAccess(associationSettings, resolvedObjectType),
      value: isOneToMany ? selectedCampaignGuids : selectedCampaignGuids[0],
      onChange: handleCampaignChange,
      assetObjectType: objectType,
      editorSelect: editorSelect,
      multi: isOneToMany,
      formControlProps: Object.assign({
        tooltip: isOneToMany ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "campaignSelect.oneToManyTooltip_jsx",
          options: {
            url: 'https://knowledge.hubspot.com/campaigns/understand-campaigns',
            use: 'link',
            useZorse: true,
            padding: '0'
          },
          elements: {
            Button: KnowledgeBaseButton
          }
        }) : undefined,
        tooltipPlacement: 'right',
        label: /*#__PURE__*/_jsx(CampaignsPlatformSelectLabel, {
          associationSettings: associationSettings,
          multi: isOneToMany,
          objectType: resolvedObjectType
        }),
        FormLabel
      }, formControlStyle),
      formHelpAsParagraph: Boolean(FormLabel),
      helpAsTooltip: isOneToMany,
      assetLabel: objectDefinition.singularForm,
      disabled: disabled,
      disabledTooltip: disabledTooltip,
      objectBuilderUiProps: objectBuilderUiProps,
      onBlur: clearCache,
      onOptionsLoaded: handleOptionsLoaded
    })]
  });
}

/**
 * @temporary Remove gate check and old CampaignsPlatformSelect once fully ungated for CampaignsSelectV2
 * @expiration 2026-08-15
 * @deprecated
 */
const CampaignsPlatformSelectGated = /*#__PURE__*/memo(props => {
  if (getIsUngatedForCampaignsSelectV2()) {
    return /*#__PURE__*/_jsx(CampaignsPlatformSelectV2, Object.assign({}, props));
  }
  return /*#__PURE__*/_jsx(CampaignsPlatformSelect, Object.assign({}, props));
});
export default CampaignsPlatformSelectGated;